import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router';
import { trpc } from '@org/clients/school';
import { Button, ProtectedRoute, Badge, Switch } from '@org/ui';
import { StarRatingDisplay } from '@org/ui';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

export const Route = createFileRoute('/feedback')({
  component: FeedbackRoute,
});

function FeedbackRoute() {
  const navigate = useNavigate({ from: '/feedback' });
  const { t } = useTranslation('admin');
  const { data: campaigns, isLoading } = trpc.feedback.list.useQuery();
  const utils = trpc.useUtils();

  const toggleMutation = trpc.feedback.update.useMutation({
    onSuccess: () => {
      utils.feedback.list.invalidate();
    },
    onError: () => {
      toast.error(t('common.toasts.actionError', { action: 'update', entity: 'campaign' }));
    },
  });

  return (
    <ProtectedRoute requiredPermissions={['admin:all']}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">{t('feedback.page.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('feedback.page.subtitle')}</p>
          </div>
          <Button variant="outline" onClick={() => navigate({ to: '/feedback/new' })}>
            {t('feedback.page.createButton')}
          </Button>
        </div>

        {isLoading ? (
          <div className="py-12 text-center text-sm text-muted-foreground">Loading...</div>
        ) : !campaigns || campaigns.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            {t('feedback.list.empty')}
          </div>
        ) : (
          <div className="rounded-lg border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium">{t('feedback.list.title')}</th>
                  <th className="px-4 py-3 text-left font-medium">{t('feedback.list.type')}</th>
                  <th className="px-4 py-3 text-left font-medium">{t('feedback.list.target')}</th>
                  <th className="px-4 py-3 text-left font-medium">{t('feedback.list.status')}</th>
                  <th className="px-4 py-3 text-left font-medium">{t('feedback.list.questions')}</th>
                  <th className="px-4 py-3 text-left font-medium">{t('feedback.list.responses')}</th>
                  <th className="px-4 py-3 text-left font-medium">{t('feedback.list.avgRating')}</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((campaign) => (
                  <tr
                    key={campaign.campaignId}
                    className="cursor-pointer border-b transition hover:bg-muted/30"
                    onClick={() =>
                      navigate({
                        to: '/feedback/$campaignId',
                        params: { campaignId: campaign.campaignId },
                      })
                    }
                  >
                    <td className="px-4 py-3 font-medium">{campaign.title}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline">
                        {t(`feedback.list.typeLabels.${campaign.campaignType}`)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      {t(`feedback.list.targetLabels.${campaign.targetType}`)}
                    </td>
                    <td className="px-4 py-3">
                      <Switch
                        checked={campaign.status === 'active'}
                        onCheckedChange={(checked) => {
                          toggleMutation.mutate({
                            campaignId: campaign.campaignId,
                            status: checked ? 'active' : 'closed',
                          });
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td className="px-4 py-3 text-center">{campaign.questionCount}</td>
                    <td className="px-4 py-3 text-center">{campaign.responseCount}</td>
                    <td className="px-4 py-3">
                      {campaign.avgRating ? (
                        <StarRatingDisplay value={Number(campaign.avgRating)} size={14} />
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Outlet />
      </div>
    </ProtectedRoute>
  );
}
